'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, ChevronRight, ExternalLink, FileText, ArrowRight, CheckCircle2, Clock, Package, Receipt, MapPin } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';

// ─── Data ─────────────────────────────────────────────────────────────────────

const allSOs = [
  { id: 'so-3244', soNumber: 'SO-3244', jobId: 'JOB-CRM-9924', jobRef: 'job-crm-9924', jobName: 'Merch drop — TechConf 2026', linkedPo: 'PO-2854', linkedPoId: 'po-2854', client: 'TechConf Events Ltd', createdDate: '16/08/2026', total: '£13,200', totalRaw: 13200, status: 'draft', invoiceStatus: '—', invoiceNumber: '—' },
  { id: 'so-3243', soNumber: 'SO-3243', jobId: 'JOB-CRM-9924', jobRef: 'job-crm-9924', jobName: 'Merch drop — TechConf 2026', linkedPo: 'PO-2853', linkedPoId: 'po-2853', client: 'TechConf Events Ltd', createdDate: '15/08/2026', total: '£8,160', totalRaw: 8160, status: 'fulfilled', invoiceStatus: 'overdue', invoiceNumber: 'INV-10482' },
  { id: 'so-3242', soNumber: 'SO-3242', jobId: 'JOB-CRM-9924', jobRef: 'job-crm-9924', jobName: 'Merch drop — TechConf 2026', linkedPo: 'PO-2852', linkedPoId: 'po-2852', client: 'TechConf Events Ltd', createdDate: '15/08/2026', total: '£9,840', totalRaw: 9840, status: 'confirmed', invoiceStatus: '—', invoiceNumber: '—' },
  { id: 'so-3241', soNumber: 'SO-3241', jobId: 'JOB-CRM-9924', jobRef: 'job-crm-9924', jobName: 'Merch drop — TechConf 2026', linkedPo: 'PO-2851', linkedPoId: 'po-2851', client: 'TechConf Events Ltd', createdDate: '15/08/2026', total: '£14,880', totalRaw: 14880, status: 'confirmed', invoiceStatus: '—', invoiceNumber: '—' },
  { id: 'so-3239', soNumber: 'SO-3239', jobId: 'JOB-CRM-9921', jobRef: 'job-crm-9921', jobName: 'Branded hoodies — RetailCo Q3', linkedPo: 'PO-2848', linkedPoId: 'po-2848', client: 'RetailCo UK', createdDate: '13/08/2026', total: '£11,520', totalRaw: 11520, status: 'confirmed', invoiceStatus: 'overdue', invoiceNumber: 'INV-10477' },
  { id: 'so-3238', soNumber: 'SO-3238', jobId: 'JOB-CRM-9921', jobRef: 'job-crm-9921', jobName: 'Branded hoodies — RetailCo Q3', linkedPo: 'PO-2847', linkedPoId: 'po-2847', client: 'RetailCo UK', createdDate: '13/08/2026', total: '£15,840', totalRaw: 15840, status: 'invoiced', invoiceStatus: 'due', invoiceNumber: 'INV-10481' },
  { id: 'so-3232', soNumber: 'SO-3232', jobId: 'JOB-CRM-9919', jobRef: 'job-crm-9919', jobName: 'Promo kit — SaaS Summit', linkedPo: 'PO-2842', linkedPoId: 'po-2842', client: 'SaaS Summit Ltd', createdDate: '11/08/2026', total: '£8,640', totalRaw: 8640, status: 'confirmed', invoiceStatus: '—', invoiceNumber: '—' },
  { id: 'so-3231', soNumber: 'SO-3231', jobId: 'JOB-CRM-9919', jobRef: 'job-crm-9919', jobName: 'Promo kit — SaaS Summit', linkedPo: 'PO-2843', linkedPoId: 'po-2843', client: 'SaaS Summit Ltd', createdDate: '11/08/2026', total: '£11,280', totalRaw: 11280, status: 'confirmed', invoiceStatus: 'sent', invoiceNumber: 'INV-10473' },
  { id: 'so-3225', soNumber: 'SO-3225', jobId: 'JOB-CRM-9917', jobRef: 'job-crm-9917', jobName: 'Tote bags — GreenFest', linkedPo: 'PO-2838', linkedPoId: 'po-2838', client: 'GreenFest CIC', createdDate: '08/08/2026', total: '£5,760', totalRaw: 5760, status: 'fulfilled', invoiceStatus: 'paid', invoiceNumber: 'INV-10480' },
  { id: 'so-3218', soNumber: 'SO-3218', jobId: 'JOB-CRM-9915', jobRef: 'job-crm-9915', jobName: 'Embroidered polos — HealthOrg', linkedPo: 'PO-2835', linkedPoId: 'po-2835', client: 'HealthOrg NHS', createdDate: '05/08/2026', total: '£37,200', totalRaw: 37200, status: 'invoiced', invoiceStatus: 'sent', invoiceNumber: 'INV-10478' },
  { id: 'so-3211', soNumber: 'SO-3211', jobId: 'JOB-CRM-9912', jobRef: 'job-crm-9912', jobName: 'Caps and beanies — SportsBrand', linkedPo: 'PO-2831', linkedPoId: 'po-2831', client: 'SportsBrand Ltd', createdDate: '01/08/2026', total: '£21,840', totalRaw: 21840, status: 'invoiced', invoiceStatus: 'overdue', invoiceNumber: 'INV-10476' },
  { id: 'so-3201', soNumber: 'SO-3201', jobId: 'JOB-CRM-9908', jobRef: 'job-crm-9908', jobName: 'Desk kits — FinanceCorp', linkedPo: 'PO-2821', linkedPoId: 'po-2821', client: 'FinanceCorp PLC', createdDate: '28/07/2026', total: '£11,760', totalRaw: 11760, status: 'fulfilled', invoiceStatus: 'reconciled', invoiceNumber: 'INV-10474' },
];

const soLineItems: Record<string, { id: string; description: string; qty: number; unitPrice: string; total: string }[]> = {
  'so-3243': [
    { id: 'li-so3243-1', description: 'Lanyards — dye-sub print, 1000 units', qty: 1000, unitPrice: '£6.80', total: '£6,800' },
    { id: 'li-so3243-2', description: 'Artwork and proofing', qty: 1, unitPrice: '£800.00', total: '£800' },
    { id: 'li-so3243-3', description: 'Express delivery', qty: 1, unitPrice: '£560.00', total: '£560' },
  ],
  'so-3241': [
    { id: 'li-so3241-1', description: 'Gildan 18500 hoodie — embroidered TechConf logo — S/M/L/XL', qty: 250, unitPrice: '£55.20', total: '£13,800' },
    { id: 'li-so3241-2', description: 'Embroidery setup and digitising', qty: 1, unitPrice: '£1,080.00', total: '£1,080' },
  ],
  'so-3218': [
    { id: 'li-so3218-1', description: 'Gildan 64000 polo — embroidered NHS trust logo — assorted', qty: 450, unitPrice: '£76.00', total: '£34,200' },
    { id: 'li-so3218-2', description: 'Embroidery setup — 5 trust variants', qty: 5, unitPrice: '£144.00', total: '£720' },
    { id: 'li-so3218-3', description: 'Packing and labelling per NHS spec', qty: 1, unitPrice: '£2,280.00', total: '£2,280' },
  ],
  'so-3238': [
    { id: 'li-so3238-1', description: 'Gildan 18500 hoodie navy — embroidered logo — M/L/XL', qty: 150, unitPrice: '£97.60', total: '£14,640' },
    { id: 'li-so3238-2', description: 'Embroidery setup', qty: 1, unitPrice: '£1,200.00', total: '£1,200' },
  ],
};

const defaultLineItems = [
  { id: 'li-so-default-1', description: 'Branded merchandise — see linked PO for detail', qty: 1, unitPrice: '—', total: '—' },
];

// ─── Delivery address keyed by SO ID (1 per SO) ───────────────────────────────

interface DeliveryAddress {
  id: string;
  label: string;
  recipient: string;
  line1: string;
  line2?: string;
  city: string;
  postcode: string;
  country: string;
  notes?: string;
}

const deliveryAddressBySo: Record<string, DeliveryAddress> = {
  'so-3244': { id: 'da-so3244', label: 'Main venue', recipient: 'TechConf Events Ltd — Goods In', line1: 'ExCeL London, One Western Gateway', city: 'London', postcode: 'E16 1XL', country: 'UK', notes: 'Deliver to loading bay B, Mon–Fri 08:00–16:00' },
  'so-3243': { id: 'da-so3243', label: 'Conference centre', recipient: 'TechConf Events Ltd — Receiving', line1: 'ExCeL London, One Western Gateway', city: 'London', postcode: 'E16 1XL', country: 'UK', notes: 'Attn: Logistics Manager — ref PO-2853' },
  'so-3242': { id: 'da-so3242', label: 'Primary delivery', recipient: 'TechConf Events Ltd', line1: 'ExCeL London, One Western Gateway', city: 'London', postcode: 'E16 1XL', country: 'UK' },
  'so-3241': { id: 'da-so3241', label: 'Main venue', recipient: 'TechConf Events Ltd — Goods In', line1: 'ExCeL London, One Western Gateway', city: 'London', postcode: 'E16 1XL', country: 'UK', notes: 'Deliver to loading bay B' },
  'so-3239': { id: 'da-so3239', label: 'Head office', recipient: 'RetailCo UK — Merchandise Team', line1: '200 Deansgate', city: 'Manchester', postcode: 'M3 3WB', country: 'UK', notes: 'Contact Sarah Booth on arrival: 07700 900 123' },
  'so-3238': { id: 'da-so3238', label: 'Distribution centre', recipient: 'RetailCo UK — DC Inbound', line1: 'Unit 12, Trafford Park Industrial Estate', line2: 'Trafford Park', city: 'Manchester', postcode: 'M17 1PQ', country: 'UK', notes: 'Book delivery slot 48 hrs in advance' },
  'so-3232': { id: 'da-so3232', label: 'Summit venue', recipient: 'SaaS Summit Ltd — Events Team', line1: 'The Brewery, 52 Chiswell Street', city: 'London', postcode: 'EC1Y 4SD', country: 'UK', notes: 'Deliver day before event — 18 Aug 2026' },
  'so-3231': { id: 'da-so3231', label: 'Venue goods in', recipient: 'SaaS Summit Ltd', line1: 'The Brewery, 52 Chiswell Street', city: 'London', postcode: 'EC1Y 4SD', country: 'UK' },
  'so-3225': { id: 'da-so3225', label: 'Festival site', recipient: 'GreenFest CIC — Volunteer Coordinator', line1: 'Victoria Park', line2: 'Grove Road', city: 'London', postcode: 'E3 5TB', country: 'UK', notes: 'Eco-friendly packaging required' },
  'so-3218': { id: 'da-so3218', label: 'NHS Trust HQ', recipient: 'HealthOrg NHS — Procurement', line1: 'Trust HQ, 330 Grays Inn Road', city: 'London', postcode: 'WC1X 8DA', country: 'UK', notes: 'NHS delivery compliance required — ref NHS-PO-2835' },
  'so-3211': { id: 'da-so3211', label: 'Warehouse', recipient: 'SportsBrand Ltd — Inbound Logistics', line1: 'Unit 7, Magna Park', line2: 'Lutterworth', city: 'Lutterworth', postcode: 'LE17 4XN', country: 'UK', notes: 'Pallet delivery only — contact warehouse before dispatch' },
  'so-3201': { id: 'da-so3201', label: 'Head office', recipient: 'FinanceCorp PLC — Office Manager', line1: '1 Canada Square', city: 'London', postcode: 'E14 5AB', country: 'UK', notes: 'Deliver to reception, Mon–Fri 09:00–17:00' },
};

const soStatuses = ['draft', 'confirmed', 'fulfilled', 'invoiced'];

const statusSteps = [
  { key: 'draft', label: 'Draft', icon: Clock },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle2 },
  { key: 'fulfilled', label: 'Fulfilled', icon: Package },
  { key: 'invoiced', label: 'Invoiced', icon: Receipt },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function SOLandingContent({ soId }: { soId: string }) {
  const so = allSOs.find(s => s.id === soId);
  const [currentStatus, setCurrentStatus] = useState(so?.status ?? 'draft');
  const [notes, setNotes] = useState('');

  if (!so) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-4">
        <FileText size={40} style={{ color: 'var(--border)' }} />
        <p className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Sales order not found</p>
        <p className="text-sm" style={{ color: 'var(--slate)' }}>No SO with ID &quot;{soId}&quot; exists.</p>
        <Link href="/sales-order-management" className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--accent)' }}>
          <ArrowLeft size={14} /> Back to Sales Orders
        </Link>
      </div>
    );
  }

  const lineItems = soLineItems[so.id] ?? defaultLineItems;
  const currentStepIndex = statusSteps.findIndex(s => s.key === currentStatus);
  const isOverdue = so.invoiceStatus === 'overdue';

  const handleStatusChange = (status: string) => {
    setCurrentStatus(status);
    toast.success(`SO status updated to ${status}`);
  };

  const handleGenerateInvoice = () => {
    toast.success(`AR invoice generated from ${so.soNumber}`);
    setCurrentStatus('invoiced');
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm flex-wrap" style={{ color: 'var(--slate)' }}>
        <Link href="/sales-order-management" className="hover:underline" style={{ color: 'var(--accent)' }}>Sales Orders</Link>
        <ChevronRight size={14} />
        <Link href={`/jobs/${so.jobRef}`} className="hover:underline" style={{ color: 'var(--accent)' }}>{so.jobId}</Link>
        <ChevronRight size={14} />
        <span style={{ color: 'var(--foreground)' }}>{so.soNumber}</span>
      </div>

      {/* Header */}
      <div
        className="rounded-xl border p-6"
        style={{
          backgroundColor: isOverdue ? 'var(--amber-light)' : 'var(--card)',
          borderColor: isOverdue ? '#FDE8C4' : 'var(--border)',
        }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--muted)', color: 'var(--slate)' }}>
                {so.soNumber}
              </span>
              <StatusBadge status={currentStatus as never} />
              {so.invoiceStatus !== '—' && (
                <StatusBadge status={so.invoiceStatus as never} pulse={isOverdue} />
              )}
            </div>
            <h1 className="text-2xl font-semibold mt-2" style={{ color: 'var(--foreground)', letterSpacing: '-0.01em' }}>
              {so.client}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--slate)' }}>{so.jobName}</p>
          </div>
          <Link
            href="/sales-order-management"
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border transition-colors hover:bg-muted"
            style={{ borderColor: 'var(--border)', color: 'var(--slate)' }}
          >
            <ArrowLeft size={14} />
            Back to SOs
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-5 pt-5 border-t" style={{ borderColor: isOverdue ? '#FDE8C4' : 'var(--border)' }}>
          {[
            { label: 'Job ID', value: so.jobId, href: `/jobs/${so.jobRef}` },
            { label: 'Client', value: so.client },
            { label: 'Created', value: so.createdDate },
            { label: 'SO total', value: so.total },
            { label: 'Linked PO', value: so.linkedPo, href: `/purchase-orders/${so.linkedPoId}` },
            { label: 'Invoice', value: so.invoiceNumber !== '—' ? so.invoiceNumber : 'Not yet generated' },
          ].map(f => (
            <div key={`sof-${f.label}`}>
              <p className="text-xs font-medium" style={{ color: 'var(--slate)' }}>{f.label}</p>
              {f.href && f.value !== '—' ? (
                <Link href={f.href} className="text-sm font-semibold mt-0.5 hover:underline block" style={{ color: 'var(--accent)' }}>
                  {f.value}
                </Link>
              ) : (
                <p className="text-sm font-semibold mt-0.5" style={{ color: 'var(--foreground)' }}>{f.value}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Document chain */}
      <div
        className="rounded-xl border p-5"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--foreground)' }}>Document chain</h3>
        <div className="flex items-center gap-3 flex-wrap">
          <Link href={`/jobs/${so.jobRef}`} className="text-center group">
            <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--slate)' }}>Job</p>
            <span className="text-xs font-mono font-semibold px-2.5 py-1.5 rounded-md group-hover:underline block" style={{ backgroundColor: 'var(--muted)', color: 'var(--foreground)' }}>
              {so.jobId}
            </span>
          </Link>
          <ArrowRight size={14} style={{ color: 'var(--slate)' }} />
          <Link href={`/purchase-orders/${so.linkedPoId}`} className="text-center group">
            <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--slate)' }}>Purchase order</p>
            <span className="text-xs font-mono font-semibold px-2.5 py-1.5 rounded-md group-hover:underline block" style={{ backgroundColor: 'var(--muted)', color: 'var(--foreground)' }}>
              {so.linkedPo}
            </span>
          </Link>
          <ArrowRight size={14} style={{ color: 'var(--slate)' }} />
          <div className="text-center">
            <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--slate)' }}>Sales order</p>
            <span className="text-xs font-mono font-semibold px-2.5 py-1.5 rounded-md block" style={{ backgroundColor: 'var(--teal-light)', color: 'var(--accent)' }}>
              {so.soNumber}
            </span>
          </div>
          <ArrowRight size={14} style={{ color: 'var(--slate)' }} />
          <div className="text-center">
            <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--slate)' }}>AR invoice</p>
            {so.invoiceNumber !== '—' ? (
              <div className="flex items-center gap-1.5">
                <span
                  className="text-xs font-mono font-semibold px-2.5 py-1.5 rounded-md block"
                  style={{
                    backgroundColor: isOverdue ? '#FEE2E2' : so.invoiceStatus === 'paid' || so.invoiceStatus === 'reconciled' ? 'var(--teal-light)' : 'var(--muted)',
                    color: isOverdue ? '#DC2626' : so.invoiceStatus === 'paid' || so.invoiceStatus === 'reconciled' ? 'var(--accent)' : 'var(--foreground)',
                  }}
                >
                  {so.invoiceNumber}
                </span>
                <StatusBadge status={so.invoiceStatus as never} pulse={isOverdue} />
              </div>
            ) : (
              <span className="text-xs px-2.5 py-1.5 rounded-md block" style={{ backgroundColor: 'var(--muted)', color: 'var(--slate)' }}>
                Not generated
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Delivery address */}
      {(() => {
        const addr = deliveryAddressBySo[so.id];
        return (
          <div
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
          >
            <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-2">
                <MapPin size={15} style={{ color: 'var(--accent)' }} />
                <h3 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Delivery address</h3>
              </div>
              <Link
                href={`/purchase-orders/${so.linkedPoId}`}
                className="text-xs font-mono px-2 py-0.5 rounded hover:underline"
                style={{ backgroundColor: 'var(--muted)', color: 'var(--slate)' }}
              >
                {so.linkedPo}
              </Link>
            </div>
            {!addr ? (
              <div className="px-5 py-8 text-center">
                <MapPin size={28} className="mx-auto mb-2" style={{ color: 'var(--border)' }} />
                <p className="text-sm" style={{ color: 'var(--slate)' }}>No delivery address recorded for {so.soNumber}</p>
              </div>
            ) : (
              <div className="px-5 py-4 flex items-start gap-4">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: 'var(--teal-light)', color: 'var(--accent)' }}
                >
                  <MapPin size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--accent)' }}>{addr.label}</span>
                  <p className="text-sm font-medium mt-0.5" style={{ color: 'var(--foreground)' }}>{addr.recipient}</p>
                  <p className="text-sm mt-0.5" style={{ color: 'var(--slate)' }}>
                    {addr.line1}{addr.line2 ? `, ${addr.line2}` : ''}, {addr.city}, {addr.postcode}, {addr.country}
                  </p>
                  {addr.notes && (
                    <p className="text-xs mt-1.5 px-2.5 py-1.5 rounded-md" style={{ backgroundColor: 'var(--cloud)', color: 'var(--slate)' }}>
                      📋 {addr.notes}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Status progress */}
      <div
        className="rounded-xl border p-5"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--foreground)' }}>Status</h3>

        {/* Progress stepper */}
        <div className="flex items-center gap-0 mb-5 overflow-x-auto pb-1">
          {statusSteps.map((step, idx) => {
            const StepIcon = step.icon;
            const isDone = idx < currentStepIndex;
            const isCurrent = idx === currentStepIndex;
            return (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center min-w-[80px]">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center mb-1.5 transition-all"
                    style={{
                      backgroundColor: isDone || isCurrent ? 'var(--accent)' : 'var(--muted)',
                      color: isDone || isCurrent ? 'white' : 'var(--slate)',
                    }}
                  >
                    <StepIcon size={14} />
                  </div>
                  <p className="text-xs font-medium text-center" style={{ color: isCurrent ? 'var(--accent)' : isDone ? 'var(--foreground)' : 'var(--slate)' }}>
                    {step.label}
                  </p>
                </div>
                {idx < statusSteps.length - 1 && (
                  <div
                    className="flex-1 h-0.5 mx-1 min-w-[16px]"
                    style={{ backgroundColor: idx < currentStepIndex ? 'var(--accent)' : 'var(--border)' }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Status buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          {soStatuses.map(s => (
            <button
              key={`so-status-btn-${s}`}
              onClick={() => handleStatusChange(s)}
              className="px-3 py-1.5 text-xs font-medium rounded-md border transition-all"
              style={{
                backgroundColor: currentStatus === s ? 'var(--ink)' : 'var(--card)',
                color: currentStatus === s ? 'white' : 'var(--slate)',
                borderColor: currentStatus === s ? 'var(--ink)' : 'var(--border)',
              }}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {/* Generate invoice CTA */}
        {(currentStatus === 'fulfilled' || currentStatus === 'confirmed') && so.invoiceNumber === '—' && (
          <button
            onClick={handleGenerateInvoice}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md text-white transition-all"
            style={{ backgroundColor: 'var(--accent)' }}
          >
            <ExternalLink size={14} />
            Generate AR invoice from this SO
          </button>
        )}
      </div>

      {/* Line items */}
      <div
        className="rounded-xl border overflow-hidden"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Line items</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: 'var(--cloud)', borderBottom: '1px solid var(--border)' }}>
                {['Description', 'Qty', 'Unit price', 'Total'].map(h => (
                  <th key={`so-li-th-${h}`} className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--slate)', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lineItems.map(li => (
                <tr key={li.id} className="border-b last:border-0" style={{ borderColor: 'var(--border)' }}>
                  <td className="px-5 py-3" style={{ color: 'var(--foreground)' }}>{li.description}</td>
                  <td className="px-5 py-3 tabular-nums" style={{ color: 'var(--slate)' }}>{li.qty}</td>
                  <td className="px-5 py-3 tabular-nums" style={{ color: 'var(--slate)' }}>{li.unitPrice}</td>
                  <td className="px-5 py-3 tabular-nums font-semibold" style={{ color: 'var(--accent)' }}>{li.total}</td>
                </tr>
              ))}
              <tr style={{ backgroundColor: 'var(--cloud)', borderTop: '1px solid var(--border)' }}>
                <td colSpan={3} className="px-5 py-3 font-semibold text-right" style={{ color: 'var(--foreground)' }}>SO total</td>
                <td className="px-5 py-3 tabular-nums font-bold text-lg" style={{ color: 'var(--accent)' }}>{so.total}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes */}
      <div
        className="rounded-xl border p-5"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--foreground)' }}>Notes</h3>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Add notes about this sales order — client approvals, amendments, delivery instructions…"
          rows={4}
          className="w-full px-3 py-2.5 text-sm rounded-md border outline-none resize-none"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)' }}
        />
        <div className="flex items-center gap-3 mt-3">
          <button
            onClick={() => { if (notes.trim()) toast.success('Notes saved'); }}
            disabled={!notes.trim()}
            className="px-4 py-2 text-sm font-medium rounded-md text-white transition-all disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent)' }}
          >
            Save notes
          </button>
          <Link
            href={`/purchase-orders/${so.linkedPoId}`}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border transition-colors hover:bg-muted"
            style={{ borderColor: 'var(--border)', color: 'var(--slate)' }}
          >
            <ExternalLink size={14} />
            View linked PO
          </Link>
        </div>
      </div>
    </div>
  );
}
